import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import type {
  ChatMessage,
  ChatMessageResponse,
  ConversationalFormOption,
} from '@karehero/models';
import { randomUUID } from '@karehero/helpers/string';

import { useDispatch, useSelector } from 'store';
import {
  useAppendChatMessageMutation,
  useCreateChatSessionMutation,
  useListChatSessionsQuery,
} from 'store/api/chatSession';
import { getRejectedMessageIds } from 'store/slices/benefitsCalculatorSlice';
import { newToast } from 'store/slices/uiSlice';
import {
  askedSteps,
  CARE_RECIPIENT_PLACEHOLDER,
  type BenefitsCalculatorAnswers,
  type BenefitsCalculatorStep,
} from 'config/benefitsCalculatorV2Steps';

/** The pause before the next question, matching onboarding's. */
const THINK_DELAY_MS = 900;

const CHAT_SESSION_MODE = 'benefits-calculator';

/**
 * The answers a session's messages hold, by question, with the message each
 * came from. A later answer to a question replaces an earlier one.
 */
const resumeFrom = (messages: ChatMessageResponse[]) => {
  const answers: BenefitsCalculatorAnswers = {};
  const messageIDs: Record<string, string> = {};
  const failedMessageIDs: string[] = [];

  for (const message of messages) {
    if (message.content.kind !== 'answers') continue;
    for (const answer of message.content.answers) {
      // The calculator's questions are all answered by options.
      answers[answer.questionId] = answer.optionIds ?? [];
      messageIDs[answer.questionId] = message.id;
    }
    if (message.status === 'error') failedMessageIDs.push(message.id);
  }

  return { answers, messageIDs, failedMessageIDs };
};

const textMessage = (
  id: string,
  from: ChatMessage['from'],
  text: string,
  status: ChatMessage['status'] = 'sent',
): ChatMessage => ({
  id,
  from,
  content: { kind: 'text', text },
  status,
});

const withName = (text: string, name: string) =>
  text.replaceAll(CARE_RECIPIENT_PLACEHOLDER, name);

const answerLabel = (step: BenefitsCalculatorStep, optionIDs: string[]) =>
  (step.options ?? [])
    .filter((option) => optionIDs.includes(option.id))
    .map((option) => option.label)
    .join(', ');

export interface UseBenefitsCalculatorChatOptions {
  /** Said in place of the care recipient in each question. */
  careRecipientName: string;
  /** The care circle the chat, and so its answers, belong to. */
  careCircleID: string;
  /** The active session to pick up. Omit, or give one that isn't active, for the latest. */
  chatSessionID?: string;
}

/**
 * useBenefitsCalculatorChat runs the v2 benefits calculator as a chat: it asks
 * each question the answers so far call for, and sends each answer to the
 * chat session as an answers message, which the care profile saves. An answer
 * the care profile turns down does not count: its question is asked again.
 */
export const useBenefitsCalculatorChat = ({
  careRecipientName,
  careCircleID,
  chatSessionID,
}: UseBenefitsCalculatorChatOptions) => {
  const dispatch = useDispatch();
  const [createChatSession] = useCreateChatSessionMutation();
  const [appendChatMessage] = useAppendChatMessageMutation();
  const rejectedMessageIDs = useSelector(getRejectedMessageIds);

  const [sessionID, setSessionID] = useState<string>();
  const [hasSessionFailed, setHasSessionFailed] = useState(false);
  const [answers, setAnswers] = useState<BenefitsCalculatorAnswers>({});
  // The answers message each question was answered by, so a rejection can find its reply.
  const [messageIDs, setMessageIDs] = useState<Record<string, string>>({});
  // Replies the session already holds as failed, from before this visit.
  const [failedMessageIDs, setFailedMessageIDs] = useState<string[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSessionResolvedRef = useRef(false);
  // The care circle shown now, so a reply to a request made for another is dropped.
  const careCircleIDRef = useRef(careCircleID);

  // Refetched on each visit: the server changes messages too, such as marking
  // a reply rejected.
  const { data: activeSessions, isFetching: isFetchingSessions } =
    useListChatSessionsQuery(
      {
        careCircleId: careCircleID,
        mode: CHAT_SESSION_MODE,
        status: 'active',
      },
      { skip: !careCircleID, refetchOnMountOrArgChange: true },
    );

  // Another care circle starts over: its own session, its own answers.
  useEffect(() => {
    careCircleIDRef.current = careCircleID;
    isSessionResolvedRef.current = false;
    setSessionID(undefined);
    setAnswers({});
    setMessageIDs({});
    setFailedMessageIDs([]);
  }, [careCircleID]);

  // Picks up where the last visit left off, or starts a session if there's none.
  useEffect(() => {
    if (!careCircleID || !activeSessions || isFetchingSessions) return;
    if (isSessionResolvedRef.current) return;
    isSessionResolvedRef.current = true;

    // Most recently updated first.
    const session =
      activeSessions.find((s) => s.session.id === chatSessionID) ??
      activeSessions[0];
    if (session) {
      const resumed = resumeFrom(session.messages);
      setAnswers(resumed.answers);
      setMessageIDs(resumed.messageIDs);
      setFailedMessageIDs(resumed.failedMessageIDs);
      setSessionID(session.session.id);
      return;
    }

    createChatSession({
      careCircleId: careCircleID,
      body: { mode: CHAT_SESSION_MODE },
    })
      .unwrap()
      .then((session) => {
        if (careCircleIDRef.current !== careCircleID) return;
        setSessionID(session.id);
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (careCircleIDRef.current !== careCircleID) return;
        dispatch(
          newToast({
            title: 'Something went wrong',
            description:
              'We could not start the benefits calculator. Please try again.',
            variant: 'error',
          }),
        );
        setHasSessionFailed(true);
      });
  }, [
    careCircleID,
    chatSessionID,
    activeSessions,
    isFetchingSessions,
    createChatSession,
    dispatch,
  ]);

  useEffect(() => {
    if (!isThinking) return;
    const timer = setTimeout(() => setIsThinking(false), THINK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isThinking]);

  // Replies the care profile turned down, on this visit or an earlier one.
  const rejectedIDs = useMemo(
    () => new Set<string>([...rejectedMessageIDs, ...failedMessageIDs]),
    [rejectedMessageIDs, failedMessageIDs],
  );
  // Only an accepted answer counts towards what is asked next.
  const acceptedAnswers = useMemo(() => {
    const accepted: BenefitsCalculatorAnswers = {};
    for (const [questionID, optionIDs] of Object.entries(answers)) {
      if (!rejectedIDs.has(messageIDs[questionID])) {
        accepted[questionID] = optionIDs;
      }
    }
    return accepted;
  }, [answers, messageIDs, rejectedIDs]);

  const steps = useMemo(() => askedSteps(acceptedAnswers), [acceptedAnswers]);
  const current = steps.find((step) => !acceptedAnswers[step.id]);

  const messages = useMemo(() => {
    const thread: ChatMessage[] = [];
    for (const step of steps) {
      const answer = answers[step.id];
      // The next question waits behind the dots.
      if (!answer && isThinking) break;
      thread.push(
        textMessage(
          `${step.id}-ask`,
          'system',
          withName(step.ask, careRecipientName),
        ),
      );
      if (!answer) break;
      const messageID = messageIDs[step.id];
      const isRejected = rejectedIDs.has(messageID);
      thread.push(
        textMessage(
          messageID,
          'user',
          answerLabel(step, answer),
          isRejected ? 'error' : 'sent',
        ),
      );
      // The thread stops at a rejected reply: its question is asked again.
      if (isRejected) break;
    }
    return thread;
  }, [steps, answers, messageIDs, rejectedIDs, isThinking, careRecipientName]);

  // Moves on only once the answer is in the chat session; otherwise the
  // question stays to be answered again.
  const answer = async (options: ConversationalFormOption[]) => {
    if (!current || !sessionID || options.length === 0 || isSubmitting) {
      return;
    }
    const optionIDs = options.map((option) => option.id);
    const messageID = randomUUID();

    setIsSubmitting(true);
    try {
      await appendChatMessage({
        careCircleId: careCircleID,
        sessionId: sessionID,
        body: {
          id: messageID,
          senderKind: 'user',
          contentKind: 'answers',
          content: {
            kind: 'answers',
            text: answerLabel(current, optionIDs),
            answers: [{ questionId: current.id, optionIds: optionIDs }],
          },
          status: 'sent',
        },
      }).unwrap();
    } catch (err) {
      Sentry.captureException(err);
      dispatch(
        newToast({
          title: 'Something went wrong',
          description: 'We could not save that answer. Please try again.',
          variant: 'error',
        }),
      );
      return;
    } finally {
      setIsSubmitting(false);
    }

    if (careCircleIDRef.current !== careCircleID) return;
    setAnswers((previous) => ({ ...previous, [current.id]: optionIDs }));
    setMessageIDs((previous) => ({ ...previous, [current.id]: messageID }));
    setIsThinking(true);
  };

  const currentStep = current && {
    ...current,
    ask: withName(current.ask, careRecipientName),
  };

  return {
    answers: acceptedAnswers,
    messages,
    currentStep,
    sessionID,
    isSessionReady: !!sessionID,
    hasSessionFailed,
    isThinking,
    isSubmitting,
    // TODO(ENG-3736): move on to the result.
    isComplete: !current,
    answerWithOption: (option: ConversationalFormOption) => answer([option]),
    answerWithOptions: answer,
  };
};
